import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { ModalAtom, TextFieldAtom } from "../../../../components/atoms/atom";

const CostTrackingDownloadModal = ({ show, onClose, onDownload, projectName, hasAnyGenerals }) => {
    const { t } = useTranslation();
    const [language, setLanguage] = useState("de");
    const [format, setFormat] = useState("pdf");
    const [filename, setFilename] = useState("");
    const [onlyWithData, setOnlyWithData] = useState(true);
    const [showGross, setShowGross] = useState(true);

    // When generals are present, gross is the only valid view
    const canToggleGross = hasAnyGenerals === false;

    // Reset to defaults whenever the modal opens
    useEffect(() => {
        if (show) {
            setLanguage("de");
            setFormat("pdf");
            setFilename("");
            setOnlyWithData(true);
            setShowGross(true);
        }
    }, [show]);

    const ext = format === "excel" ? ".xlsx" : ".pdf";
    const defaultFilenameWithoutExt = projectName ? projectName : "Kostencontrolling";
    const defaultFilename = `${defaultFilenameWithoutExt}${ext}`;

    const handleDownload = () => {
        const finalFilename = filename.trim() ? `${filename.trim()}${ext}` : defaultFilename;
        onDownload({ language, format, filename: finalFilename, onlyWithData, showGross });
    };

    const body = (
        <div className="container-fluid">
            <Row className="gy-4">
                {/* Format */}
                <Col md={6}>
                    <h6 className="mb-3">{t("costTracking.report-format")}</h6>
                    <div className="border rounded p-3 bg-light">
                        <Row>
                            <Col xs={6}>
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="ctDownloadFormat"
                                        id="ctFormatPdf"
                                        value="pdf"
                                        checked={format === "pdf"}
                                        onChange={() => setFormat("pdf")}
                                    />
                                    <label className="form-check-label" htmlFor="ctFormatPdf">
                                        📄 PDF
                                    </label>
                                </div>
                            </Col>
                            <Col xs={6}>
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="ctDownloadFormat"
                                        id="ctFormatExcel"
                                        value="excel"
                                        checked={format === "excel"}
                                        onChange={() => setFormat("excel")}
                                    />
                                    <label className="form-check-label" htmlFor="ctFormatExcel">
                                        📊 Excel
                                    </label>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </Col>

                {/* Language */}
                <Col md={6}>
                    <h6 className="mb-3">{t("supplier-info.pdf-language")}</h6>
                    <div className="border rounded p-3 bg-light">
                        <Row>
                            <Col xs={6}>
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="ctDownloadLanguage"
                                        id="ctLangDE"
                                        value="de"
                                        checked={language === "de"}
                                        onChange={() => setLanguage("de")}
                                    />
                                    <label className="form-check-label" htmlFor="ctLangDE">
                                        🇩🇪 Deutsch
                                    </label>
                                </div>
                            </Col>
                            <Col xs={6}>
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="ctDownloadLanguage"
                                        id="ctLangEN"
                                        value="en"
                                        checked={language === "en"}
                                        onChange={() => setLanguage("en")}
                                    />
                                    <label className="form-check-label" htmlFor="ctLangEN">
                                        🇬🇧 English
                                    </label>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </Col>

                {/* Filename */}
                <Col xs={12}>
                    <TextFieldAtom
                        label={t("supplier-info.pdf-filename")}
                        placeholder={defaultFilenameWithoutExt}
                        value={filename}
                        onChange={(e) => setFilename(e.target.value)}
                        hint={`Leave blank to use default: ${defaultFilename}`}
                        suffix={ext}
                    />
                </Col>

                {/* Cost Groups Filter */}
                <Col xs={12}>
                    <h6 className="mb-3">{t("costTracking.cost-groups-display")}</h6>
                    <div className="border rounded p-3 bg-light">
                        <Row>
                            <Col xs={6}>
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="ctCostGroupsFilter"
                                        id="ctGroupsWithData"
                                        value="withData"
                                        checked={onlyWithData === true}
                                        onChange={() => setOnlyWithData(true)}
                                    />
                                    <label className="form-check-label" htmlFor="ctGroupsWithData">
                                        {t("costTracking.cost-groups-with-data")}
                                    </label>
                                </div>
                            </Col>
                            <Col xs={6}>
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="ctCostGroupsFilter"
                                        id="ctGroupsAll"
                                        value="all"
                                        checked={onlyWithData === false}
                                        onChange={() => setOnlyWithData(false)}
                                    />
                                    <label className="form-check-label" htmlFor="ctGroupsAll">
                                        {t("costTracking.cost-groups-all")}
                                    </label>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </Col>

                {/* Net / Gross toggle - only shown when there are no generals (same rule as the page filter) */}
                {canToggleGross && (
                    <Col xs={12}>
                        <h6 className="mb-3">{t("costTracking.values-display")}</h6>
                        <div className="border rounded p-3 bg-light">
                            <Row>
                                <Col xs={6}>
                                    <div className="form-check">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="ctValuesDisplay"
                                            id="ctValuesGross"
                                            value="gross"
                                            checked={showGross === true}
                                            onChange={() => setShowGross(true)}
                                        />
                                        <label className="form-check-label" htmlFor="ctValuesGross">
                                            {t("costTracking.brutto")}
                                        </label>
                                    </div>
                                </Col>
                                <Col xs={6}>
                                    <div className="form-check">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="ctValuesDisplay"
                                            id="ctValuesNet"
                                            value="net"
                                            checked={showGross === false}
                                            onChange={() => setShowGross(false)}
                                        />
                                        <label className="form-check-label" htmlFor="ctValuesNet">
                                            {t("costTracking.netto")}
                                        </label>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </Col>
                )}
            </Row>
        </div>
    );

    return (
        <ModalAtom
            show={show}
            handleClose={onClose}
            handleSubmit={handleDownload}
            title={t("costTracking.download-report")}
            saveText="common.download"
            body={body}
            size="lg"
        />
    );
};

export default CostTrackingDownloadModal;
